import React, { useState, useEffect } from 'react';
import { supabase, safeSupabaseOperation } from '../../lib/supabase';
import { Link, useNavigate, Outlet, useLocation } from 'react-router-dom';
import {
  BarChart3,
  Users,
  FileText,
  MapPin,
  Package,
  MessageSquare,
  Settings,
  Star,
  Handshake,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Home,
  Bell,
  Search,
  User,
  ChevronDown,
  ArrowRight,
  Globe,
  PenTool,
  Layers,
  DollarSign,
  Zap,
  GraduationCap,
  Briefcase,
  Image
} from 'lucide-react';
import EmptyState from '../../components/admin/EmptyState';
import LoadingState from '../../components/admin/LoadingState';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import DashboardStats from '../../components/admin/DashboardStats';
import ActivityFeed from '../../components/admin/ActivityFeed';
import QuickActions from '../../components/admin/QuickActions';
import DashboardCharts from '../../components/admin/DashboardCharts';
import toast from 'react-hot-toast';

const Dashboard: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [userData, setUserData] = useState<any>(null);
  const [stats, setStats] = useState({
    products: 0,
    locations: 0,
    blogPosts: 0,
    partners: 0
  });
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        // Check if user is authenticated
        if (supabase) {
          const { data: { session } } = await supabase.auth.getSession();
          if (!session) {
            // Check for demo session in localStorage
            const demoSession = localStorage.getItem('demoSession');
            if (demoSession) {
              const parsedSession = JSON.parse(demoSession);
              // Check if demo session is expired
              if (parsedSession.expires_at > Date.now()) {
                setUserData(parsedSession.user);
              } else {
                localStorage.removeItem('demoSession');
                navigate('/admin/login');
                return;
              }
            } else {
              navigate('/admin/login');
              return;
            }
          } else {
            setUserData(session.user);
          }
        } else {
          // Check for demo session in localStorage
          const demoSession = localStorage.getItem('demoSession');
          if (demoSession) {
            const parsedSession = JSON.parse(demoSession);
            // Check if demo session is expired
            if (parsedSession.expires_at > Date.now()) {
              setUserData(parsedSession.user);
            } else {
              localStorage.removeItem('demoSession');
              navigate('/admin/login');
              return;
            }
          } else {
            navigate('/admin/login');
            return;
          }
        }
        
        // Load stats
        await loadStats();
        
        // Load recent activity
        await loadRecentActivity();
        
        setIsLoading(false);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        setIsLoading(false);
      }
    };

    loadDashboardData();
    
    // Add scroll event listener
    const handleScroll = () => {
      if (window.scrollY > 10) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navigate]);

  const loadStats = async () => {
    try {
      // Default stats for offline mode
      const defaultStats = {
        products: 3,
        locations: 5,
        blogPosts: 5,
        partners: 4
      };
      
      if (!supabase) {
        setStats(defaultStats);
        return;
      }
      
      // Use safe operation to handle network errors gracefully
      const { data: productsCount, isOffline: productsOffline } = await safeSupabaseOperation(async () => {
        const { count, error } = await supabase
          .from('products')
          .select('*', { count: 'exact', head: true });
        
        if (error) throw error;
        return count || 0;
      }, 3);
      
      const { data: locationsCount, isOffline: locationsOffline } = await safeSupabaseOperation(async () => {
        const { count, error } = await supabase
          .from('locations')
          .select('*', { count: 'exact', head: true });
        
        if (error) throw error;
        return count || 0;
      }, 5);
      
      const { data: blogPostsCount, isOffline: blogPostsOffline } = await safeSupabaseOperation(async () => {
        const { count, error } = await supabase
          .from('blog_posts')
          .select('*', { count: 'exact', head: true });
        
        if (error) throw error;
        return count || 0;
      }, 5);
      
      const { data: partnersCount, isOffline: partnersOffline } = await safeSupabaseOperation(async () => {
        const { count, error } = await supabase
          .from('partners')
          .select('*', { count: 'exact', head: true });
        
        if (error) throw error;
        return count || 0;
      }, 4);
      
      // If any operation is offline, use default stats
      if (productsOffline || locationsOffline || blogPostsOffline || partnersOffline) {
        setStats(defaultStats);
      } else {
        setStats({
          products: productsCount || 0,
          locations: locationsCount || 0,
          blogPosts: blogPostsCount || 0,
          partners: partnersCount || 0
        });
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      // Use default stats on error
      setStats({
        products: 3,
        locations: 5,
        blogPosts: 5,
        partners: 4
      });
    }
  };

  const loadRecentActivity = async () => {
    try {
      // Default activity for offline mode
      const defaultActivity = [
        {
          id: '1',
          type: 'blog',
          title: 'La Révolution de la Mobilité Électrique en Afrique',
          date: new Date().toISOString()
        },
        {
          id: '2',
          type: 'product',
          title: 'Terra Pro',
          date: new Date(Date.now() - 86400000).toISOString() // Yesterday
        },
        {
          id: '3',
          type: 'blog',
          title: 'Impact Environnemental des Motos Électriques',
          date: new Date(Date.now() - 172800000).toISOString() // 2 days ago
        }
      ];
      
      if (!supabase) {
        setRecentActivity(defaultActivity);
        return;
      }
      
      // Use safe operation to handle network errors gracefully
      const { data: recentBlogPosts, isOffline: blogOffline } = await safeSupabaseOperation(async () => {
        const { data, error } = await supabase
          .from('blog_posts')
          .select('id, title, created_at')
          .order('created_at', { ascending: false })
          .limit(3);
        
        if (error) throw error;
        return data || [];
      }, []);
      
      const { data: recentProducts, isOffline: productsOffline } = await safeSupabaseOperation(async () => {
        const { data, error } = await supabase
          .from('products')
          .select('id, name, created_at')
          .order('created_at', { ascending: false })
          .limit(3);
        
        if (error) throw error;
        return data || [];
      }, []);
      
      // If any operation is offline, use default activity
      if (blogOffline || productsOffline) {
        setRecentActivity(defaultActivity);
      } else {
        // Combine and sort by date
        const combinedActivity = [
          ...(recentBlogPosts || []).map(post => ({
            id: post.id,
            type: 'blog',
            title: post.title,
            date: post.created_at
          })),
          ...(recentProducts || []).map(product => ({
            id: product.id,
            type: 'product',
            title: product.name,
            date: product.created_at
          }))
        ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);
        
        setRecentActivity(combinedActivity.length > 0 ? combinedActivity : defaultActivity);
      }
    } catch (error) {
      console.error('Error loading recent activity:', error);
      // Use default activity on error
      setRecentActivity([
        {
          id: '1',
          type: 'blog',
          title: 'La Révolution de la Mobilité Électrique en Afrique',
          date: new Date().toISOString()
        },
        {
          id: '2',
          type: 'product',
          title: 'Terra Pro',
          date: new Date(Date.now() - 86400000).toISOString() // Yesterday
        },
        {
          id: '3',
          type: 'blog',
          title: 'Impact Environnemental des Motos Électriques',
          date: new Date(Date.now() - 172800000).toISOString() // 2 days ago
        }
      ]);
    }
  };

  const handleLogout = async () => {
    try {
      if (supabase) {
        await supabase.auth.signOut();
      }
      // Always remove demo session
      localStorage.removeItem('demoSession');
      toast.success('Déconnexion réussie');
      navigate('/admin/login');
    } catch (error) {
      console.error('Error signing out:', error);
      toast.error('Erreur lors de la déconnexion');
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const toggleHeaderCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const mainMenuItems = [
    { path: '/admin/dashboard', icon: <BarChart3 size={20} />, label: 'Dashboard' },
    { path: '/admin/page-builder', icon: <Layers size={20} />, label: 'Page Builder' },
    { path: '/admin/crm', icon: <Users size={20} />, label: 'CRM & Leads' },
    { path: '/admin/forms', icon: <PenTool size={20} />, label: 'Forms Builder' },
    { path: '/admin/products', icon: <Package size={20} />, label: 'Produits' },
    { path: '/admin/calculators', icon: <BarChart3 size={20} />, label: 'Calculateurs' }
  ];

  const contentMenuItems = [
    { path: '/admin/blog', icon: <FileText size={20} />, label: 'Blog' },
    { path: '/admin/testimonials', icon: <Star size={20} />, label: 'Témoignages' },
    { path: '/admin/partners', icon: <Handshake size={20} />, label: 'Partenaires' },
    { path: '/admin/locations', icon: <MapPin size={20} />, label: 'Stations' },
    { path: '/admin/legal-pages', icon: <FileText size={20} />, label: 'Pages Légales' },
    { path: '/admin/case-studies', icon: <Briefcase size={20} />, label: 'Case Studies' },
    { path: '/admin/media-coverage', icon: <MessageSquare size={20} />, label: 'Médias' }
  ];

  const marketingMenuItems = [
    { path: '/admin/emails', icon: <MessageSquare size={20} />, label: 'Emails' },
    { path: '/admin/automations', icon: <Zap size={20} />, label: 'Automations' },
    { path: '/admin/seo', icon: <Search size={20} />, label: 'SEO' },
    { path: '/admin/analytics', icon: <BarChart3 size={20} />, label: 'Analytics' }
  ];

  const enterpriseMenuItems = [
    { path: '/admin/impact', icon: <Globe size={20} />, label: 'Impact & ESG' },
    { path: '/admin/multi-country', icon: <Globe size={20} />, label: 'Multi-Pays' },
    { path: '/admin/investors', icon: <DollarSign size={20} />, label: 'Investisseurs' },
    { path: '/admin/roles', icon: <Users size={20} />, label: 'Rôles' },
    { path: '/admin/activity-logs', icon: <FileText size={20} />, label: 'Logs' }
  ];

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Check if we're on the dashboard home page
  const isDashboardHome = location.pathname === '/admin' || location.pathname === '/admin/dashboard';

  if (isLoading) {
    return <LoadingState type="card" />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header 
        className={`fixed top-0 left-0 right-0 bg-white shadow-sm z-20 transition-all duration-300 ${
          isCollapsed || isScrolled ? 'h-12' : 'h-16'
        }`}
      >
        <div className="flex items-center justify-between px-4 h-full">
          <div className="flex items-center">
            <button 
              onClick={toggleSidebar}
              className="p-2 rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none"
              aria-label={isSidebarOpen ? "Fermer le menu" : "Ouvrir le menu"}
            >
              {isSidebarOpen ? <ChevronLeft size={20} /> : <Menu size={20} />}
            </button>
            
            <Link to="/admin/dashboard" className="flex items-center ml-2">
              <img 
                src="/proxy-logo.png" 
                alt="Proxy Emobility" 
                className={`transition-all duration-300 ${isCollapsed || isScrolled ? 'h-6' : 'h-8'}`} 
              />
              <span className={`ml-2 font-semibold text-gray-900 transition-all duration-300 ${isCollapsed || isScrolled ? 'text-sm' : 'text-base'}`}>
                Admin
              </span>
            </Link>
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={toggleHeaderCollapse}
              className="p-2 rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none"
              title={isCollapsed ? "Agrandir l'en-tête" : "Réduire l'en-tête"}
            >
              {isCollapsed ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
            </button>
            
            <div className={`relative ${isCollapsed || isScrolled ? 'hidden md:block' : 'block'}`}>
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
              <input
                type="text"
                placeholder="Rechercher..."
                className={`pl-9 pr-4 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-300 ${
                  isCollapsed || isScrolled ? 'w-40 text-xs' : 'w-48 text-sm'
                }`}
              />
            </div>
            
            <Link to="/" className="p-2 rounded-md text-gray-500 hover:bg-gray-100 focus:outline-none" target="_blank">
              <Globe size={20} />
            </Link>
            
            <div className="relative">
              <button className="flex items-center gap-2 p-1 rounded-md hover:bg-gray-100 focus:outline-none">
                <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center text-primary-600 font-medium">
                  {userData?.email?.charAt(0).toUpperCase() || 'A'}
                </div>
                <span className={`text-gray-700 transition-all duration-300 ${isCollapsed || isScrolled ? 'hidden md:inline text-xs' : 'text-sm'}`}>
                  {userData?.email?.split('@')[0] || 'Admin'}
                </span>
                <ChevronDown size={16} className="text-gray-500" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside 
          className={`fixed left-0 top-16 bottom-0 bg-white shadow-sm z-10 transition-all duration-300 ${
            isSidebarOpen ? (isCollapsed ? 'w-16' : 'w-64') : '-translate-x-full w-64'
          } ${isCollapsed || isScrolled ? 'top-12' : 'top-16'} overflow-y-auto`}
        >
          <div className="h-full flex flex-col justify-between py-4">
            <div className="space-y-1 px-2">
              {/* Main Menu */}
              <div className="mb-4">
                {!isCollapsed && <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Principal</h3>}
                {mainMenuItems.map((item) => (
                  <Link 
                    key={item.path}
                    to={item.path} 
                    className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                      isActive(item.path)
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {item.icon}
                    {(!isCollapsed || !isSidebarOpen) && <span>{item.label}</span>}
                  </Link>
                ))}
              </div>
              
              {/* Content Menu */}
              <div className="mb-4">
                {!isCollapsed && <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Contenu</h3>}
                {contentMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                      isActive(item.path)
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {item.icon}
                    {(!isCollapsed || !isSidebarOpen) && <span>{item.label}</span>}
                  </Link>
                ))}
              </div>

              {/* Marketing Menu */}
              <div className="mb-4">
                {!isCollapsed && <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Marketing</h3>}
                {marketingMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                      isActive(item.path)
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {item.icon}
                    {(!isCollapsed || !isSidebarOpen) && <span>{item.label}</span>}
                  </Link>
                ))}
              </div>

              {/* Enterprise Menu */}
              <div className="mb-4">
                {!isCollapsed && <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Enterprise</h3>}
                {enterpriseMenuItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                      isActive(item.path)
                        ? 'bg-primary-50 text-primary-600'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    {item.icon}
                    {(!isCollapsed || !isSidebarOpen) && <span>{item.label}</span>}
                  </Link>
                ))}
              </div>
              
              {/* Settings */}
              <div className="mb-4">
                {!isCollapsed && <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Paramètres</h3>}
                <Link 
                  to="/admin/settings" 
                  className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors ${
                    isActive('/admin/settings')
                      ? 'bg-primary-50 text-primary-600'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                >
                  <Settings size={20} />
                  {(!isCollapsed || !isSidebarOpen) && <span>Paramètres</span>}
                </Link>
              </div>
            </div>
            
            <div className="px-2">
              <button 
                onClick={() => setShowLogoutConfirm(true)}
                className="flex items-center gap-3 px-3 py-2 text-gray-700 rounded-md hover:bg-red-50 hover:text-red-600 w-full"
              >
                <LogOut size={20} />
                {(!isCollapsed || !isSidebarOpen) && <span>Déconnexion</span>}
              </button>
            </div>
          </div>
        </aside>

        {/* Main Content */}
        <main 
          className={`flex-1 transition-all duration-300 ${
            isSidebarOpen ? (isCollapsed ? 'ml-16' : 'ml-64') : 'ml-0'
          } ${isCollapsed || isScrolled ? 'pt-4' : 'pt-6'} pb-12`}
        >
          {isDashboardHome ? (
            <div className="px-6">
              <div className="flex items-center justify-between mb-6">
                <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
                
                <div className="flex items-center gap-2">
                  <Link 
                    to="/"
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 bg-white rounded-md border border-gray-300 hover:bg-gray-50"
                    target="_blank"
                  >
                    <Home size={16} />
                    <span>Voir le site</span>
                  </Link>
                </div>
              </div>
              
              {/* Stats */}
              <DashboardStats stats={stats} />
              
              {/* Charts */}
              <DashboardCharts />
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Activity Feed */}
                <div className="lg:col-span-2">
                  <ActivityFeed activities={recentActivity} />
                </div>
                
                {/* Quick Actions */}
                <div>
                  <QuickActions />
                </div>
              </div>
            </div>
          ) : (
            <Outlet />
          )}
        </main>
      </div>

      {/* Logout Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showLogoutConfirm}
        title="Déconnexion"
        message="Êtes-vous sûr de vouloir vous déconnecter ?"
        confirmLabel="Déconnexion"
        cancelLabel="Annuler"
        onConfirm={handleLogout}
        onCancel={() => setShowLogoutConfirm(false)}
        type="warning"
      />
    </div>
  );
};

export default Dashboard;